import { HostedDemoTutorial } from "./HostedDemoTutorial";
import { HostedDemoCompletion } from "./HostedDemoCompletion";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
	DEMO_STEPS,
	type DemoClientEvent,
	type HostedDemoState,
} from "@kitsulab/schema";
import { apiClient, isApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ProjectStartChoices } from "./ProjectStartChoices";
import { trackKitsuEvent } from "@/lib/kitsu";

export function HostedDemoGuide({
	children,
	startChoices = false,
	onStartProject,
}: {
	projectSlug?: string;
	children: ReactNode;
	startChoices?: boolean;
	onStartProject?: () => Promise<void>;
}) {
	const { t } = useTranslation();
	const location = useLocation();
	const [state, setState] = useState<HostedDemoState | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(false);
	const [standard, setStandard] = useState(false);
	const onCreateProject = startChoices ? () => {
		if (busy) return;
		void act(async () => {
			await onStartProject?.();
			setStandard(true);
			window.dispatchEvent(new Event("kitsulab:onboarding-update"));
		});
	} : undefined;
	const [refreshHelp, setRefreshHelp] = useState(false);
	const shown = useRef(new Set<string>());
	useEffect(() => {
		if (!startChoices || shown.current.has("choices")) return;
		shown.current.add("choices");
		void trackKitsuEvent({ eventName: "kitsu_choices_shown", step: "welcome" });
	}, [startChoices]);
	const load = useCallback(async () => {
		try {
			setState((await apiClient.get<HostedDemoState>("/api/demo")).data);
			setError(false);
		} catch {
			setError(true);
		}
	}, []);
	useEffect(() => {
		void load();
	}, [load]);
	const enabled = state?.enabled === true;
	useEffect(() => {
		if (!enabled) return;
		const timer = window.setInterval(() => {
			if (document.visibilityState === "visible") void load();
		}, 5000);
		window.addEventListener("focus", load);
		window.addEventListener("kitsulab:onboarding-update", load);
		return () => {
			window.clearInterval(timer);
			window.removeEventListener("focus", load);
			window.removeEventListener("kitsulab:onboarding-update", load);
		};
	}, [enabled, load]);
	const demo = state?.demo;
	useEffect(() => {
		if (startChoices && demo?.projectId) window.dispatchEvent(new Event("kitsulab:projects-update"));
	}, [startChoices, demo?.projectId]);
	const active = demo?.activeStep ?? "dialogue";
	const phase = demo?.journey.phase ?? "discover";
	const event = useCallback(
		async (payload: DemoClientEvent) => {
			await apiClient.post("/api/demo/events", payload);
			await load();
		},
		[load],
	);
	const act = async (fn: () => Promise<void>) => {
		setBusy(true);
		try {
			await fn();
		} catch (err) {
			const data = isApiError(err)
				? (err.response?.data as { code?: unknown } | undefined)
				: undefined;
			toast.error(
				t(
					`hostedDemo.errors.${typeof data?.code === "string" ? data.code : "unavailable"}`,
					{
						defaultValue: t("hostedDemo.connectionError"),
					},
				),
			);
		} finally {
			setBusy(false);
		}
	};
	const renderChoices = (content: ReactNode) => onCreateProject ? (
		<>
			<ProjectStartChoices demo={content} onCreateProject={onCreateProject} busy={busy} />
			{standard && children}
		</>
	) : content;
	if (onCreateProject && !state?.enabled) {
		return renderChoices(
			<p role="status" className="text-sm text-zinc-400">
				{t(error ? "hostedDemo.connectionError" : state ? "dashboard.discover.unavailable" : "common.loading")}
			</p>,
		);
	}
	if (
		!state?.enabled ||
		(standard && !startChoices)
	)
		return children;
	const buttonClass =
		onCreateProject
			? "btn-pill btn-pill-primary inline-flex w-full items-center justify-center disabled:opacity-50"
			: "rounded-lg bg-violet-600 px-3 py-2 text-sm text-white disabled:opacity-50";
	if (demo?.paused) {
		const resume = (
			<button
				className={`${onCreateProject ? "" : "fixed bottom-6 right-6 z-50"} ${buttonClass}`}
				disabled={busy}
				onClick={() =>
					void act(async () => {
						await event({ event: "guided_tour_resumed", step: active });
					})
				}
			>
				{t("hostedDemo.resume")}
			</button>
		);
		return renderChoices(resume);
	}
	const completed =
		demo && DEMO_STEPS.every((step) => demo.progress[step].completedAt);
	const progress = demo?.progress[active];
	const nextStep = DEMO_STEPS[DEMO_STEPS.indexOf(active) + 1];
	const guide = (
		<aside
			aria-label={t("hostedDemo.title")}
			className={onCreateProject ? "text-white" : `fixed bottom-6 right-6 z-50 ${completed ? "w-[min(440px,calc(100vw-32px))] max-h-[calc(100dvh-48px)] overflow-y-auto" : "w-[min(370px,calc(100vw-32px))]"} rounded-2xl border border-violet-400/40 bg-[#131017] p-5 text-white shadow-2xl`}
		>
			{!onCreateProject && <div className="flex items-start justify-between gap-3">
				<h2 className="font-semibold">{t("hostedDemo.title")}</h2>
				{demo && (
					<button
						disabled={busy}
						className="text-xs text-violet-200"
						onClick={() =>
							void act(() =>
								event({ event: "guided_tour_paused", step: active }),
							)
						}
					>
						{t("hostedDemo.hide")}
					</button>
				)}
			</div>}
			{error && (
				<p role="alert" className="mt-2 text-sm text-amber-300">
					{t("hostedDemo.connectionError")}
				</p>
			)}
			{!demo ? (
				<>
					{!onCreateProject && <><p className="my-3 text-sm text-zinc-300">{t("hostedDemo.intro")}</p>
					<p className="mb-3 text-xs text-zinc-400">
						{t("hostedDemo.requirement")}
					</p></>}
					{new URLSearchParams(location.search)
						.get("roblox")
						?.startsWith("roblox_") && (
						<p role="alert" className="mb-3 text-sm text-amber-300">
							{t("hostedDemo.linkFailed")}
						</p>
					)}
					<button
						className={buttonClass}
						disabled={busy}
						onClick={() =>
							void act(async () => {
								await trackKitsuEvent({ eventName: "kitsu_demo_selected", step: "welcome" });
								await trackKitsuEvent({
									eventName: state.linked ? "kitsu_demo_prepare_requested" : "kitsu_roblox_link_requested",
									step: "welcome",
								});
								try {
									if (!state.linked) {
										const { data } = await apiClient.post<{ url: string }>(
											"/api/auth/roblox/link",
										);
										window.location.assign(data.url);
									} else {
										await apiClient.post("/api/demo/start");
										await load();
									}
								} catch (err) {
									const response: unknown = isApiError(err) ? err.response?.data : null;
									const code = response && typeof response === "object" && "code" in response ? response.code : null;
									void trackKitsuEvent({
										eventName: state.linked ? "kitsu_demo_prepare_failed" : "kitsu_roblox_link_failed",
										step: "welcome",
										properties: { errorCode: typeof code === "string" && /^[a-z][a-z0-9_]{0,63}$/.test(code) ? code : "unavailable" },
									});
									throw err;
								}
							})
						}
					>
						{t(state.linked ? "hostedDemo.start" : onCreateProject ? "dashboard.discover.connect" : "hostedDemo.link")}
					</button>
					{!onCreateProject && <button
						className="mt-3 block text-xs text-zinc-400"
						onClick={() => {
							void trackKitsuEvent({ eventName: "kitsu_project_selected", step: "welcome" });
							setStandard(true);
						}}
					>
						{t("hostedDemo.standard")}
					</button>}
				</>
			) : phase !== "tutorial" ? (
				<>
					<p className="my-3 text-sm text-zinc-200" aria-live="polite">
						{t(
							phase === "personalize"
								? "hostedDemo.baseline.visited"
								: demo.journey.sessionReadyAt
									? "hostedDemo.baseline.arrived"
									: demo.journey.launchRequestedAt
										? "hostedDemo.baseline.waiting"
										: "hostedDemo.baseline.ready",
						)}
					</p>
					{phase === "personalize" ? (
						<button
							className={buttonClass}
							disabled={busy}
							onClick={() =>
								void act(async () => {
									await event({
										event: "demo_personalization_started",
										step: "dialogue",
									});
								})
							}
						>
							{t("hostedDemo.personalize")}
						</button>
					) : (
						<a
							className={`inline-block ${buttonClass}`}
							href={`https://www.roblox.com/games/${state.placeId}`}
							target="_blank"
							rel="noopener noreferrer"
							onClick={() =>
								void event({
									event: "demo_baseline_launch_requested",
									step: "dialogue",
								}).catch(() => setError(true))
							}
						>
							{t(onCreateProject ? "dashboard.discover.launch" : "hostedDemo.launch")}
						</a>
					)}
					{phase === "discover" && (
						<p className="mt-3 text-xs text-zinc-400">
							{t("hostedDemo.baseline.hint")}
						</p>
					)}
				</>
			) : completed ? (
				<HostedDemoCompletion busy={busy}
					onExplore={() => void act(() => event({ event: "demo_continue_exploring", step: active }))}
					onCreate={() => void act(async () => {
						await onStartProject?.();
						await event({ event: "studio_setup_started", step: active });
						setStandard(true);
					})}
				/>
			) : (
				<>
					<div className="my-3 flex gap-2">
						{DEMO_STEPS.map((step, index) => (
							<button
								key={step}
								disabled={busy}
								className={`rounded-md px-2 py-1 text-xs ${step === active ? "bg-violet-600" : "bg-zinc-800"}`}
								onClick={() =>
									void act(async () => {
										await event({ event: "guided_step_selected", step });
									})
								}
							>
								{demo.progress[step].completedAt ? "✓ " : `${index + 1}. `}
								{t(`hostedDemo.steps.${step}.name`)}
							</button>
						))}
					</div>
					<HostedDemoTutorial demo={demo} active={active} event={event}
						refresh={() => setRefreshHelp(true)}
						restore={() => void act(async () => {
							if (!window.confirm(t("hostedDemo.restoreConfirm"))) return;
							await apiClient.post("/api/demo/restore", { step: active });
							window.location.reload();
						})}
					/>
					{progress?.completedAt && nextStep && (
						<button className={`mt-3 ${buttonClass}`} disabled={busy}
							onClick={() => void act(() => event({ event: "guided_step_selected", step: nextStep }))}>
							{t(`hostedDemo.continue.${nextStep}`)}
						</button>
					)}

					{refreshHelp && progress?.savedAt && (
						<div
							role="status"
							className="mt-3 rounded-lg bg-zinc-800 p-3 text-sm"
						>
							<p>{t("hostedDemo.refreshInstructions")}</p>
							<a
								className="mt-2 inline-block text-violet-200 underline"
								href={`https://www.roblox.com/games/${state.placeId}`}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() =>
									void event({
										event: "demo_launch_requested",
										step: active,
									}).catch(() => setError(true))
								}
							>
								{t("hostedDemo.reopen")}
							</a>
						</div>
					)}
					<div className="mt-4 flex justify-between gap-3 text-xs text-zinc-400">
						{!progress?.completedAt && <button
							disabled={busy}
							onClick={() =>
								void act(async () => {
									await event({
										event: "guided_step_skipped",
										step: active,
									});
									const next =
										DEMO_STEPS[
											(DEMO_STEPS.indexOf(active) + 1) % DEMO_STEPS.length
										];
									await event({ event: "guided_step_selected", step: next });
								})
							}
						>
							{t("hostedDemo.skip")}
						</button>}
						<button
							disabled={busy}
							onClick={() =>
								void act(() =>
									event({ event: "guided_tour_dismissed", step: active }),
								)
							}
						>
							{t("hostedDemo.explore")}
						</button>
					</div>
				</>
			)}
		</aside>
	);
	return renderChoices(guide);
}
